// src/lib/http/fetch.ts
// The fetch half of the shared client, for responses axios cannot stream (SSE): cookies only, never an Authorization header.

import { readErrorEnvelope } from "./authErrors";
import { isSameOriginUrl, requestPath } from "./client";
import { decideAuthFailure, requireEnrolment } from "./stepup";

const withoutAuthorization = (headers: HeadersInit | undefined): Record<string, string> => {
	const out: Record<string, string> = {};
	if (!headers) return out;
	const entries: Array<[string, string]> =
		typeof Headers !== "undefined" && headers instanceof Headers ? Array.from(headers.entries()) : Array.isArray(headers) ? headers : Object.entries(headers);
	for (const [name, value] of entries) {
		if (name.toLowerCase() !== "authorization") out[name] = value;
	}
	return out;
};

// clone() can throw synchronously; text, not json, so readErrorEnvelope parses the body exactly once.
const errorBodyText = async (response: Response): Promise<string | undefined> => {
	try {
		return await response.clone().text();
	} catch {
		return undefined;
	}
};

// A ReadableStream body is consumed by the first attempt and cannot be sent again.
const isReplayable = (body: BodyInit | null | undefined): boolean =>
	body === undefined || body === null || typeof ReadableStream === "undefined" || !(body instanceof ReadableStream);

export const httpFetch = async (url: string, init: RequestInit = {}): Promise<Response> => {
	const sameOrigin = isSameOriginUrl(url);
	// Like the axios half: the session cookies go only to our own origin, never to a vendor.
	const send = () => fetch(url, { ...init, credentials: sameOrigin ? "include" : "omit", headers: withoutAuthorization(init.headers) });
	const sentAt = Date.now();
	const response = await send();
	if ((response.status !== 401 && response.status !== 403) || !sameOrigin) return response;
	const { code, details } = await readErrorEnvelope(await errorBodyText(response));
	const action = await decideAuthFailure(response.status, code, details, requestPath(url), sentAt, false);
	// A confined session goes to the security page (contract §6.10), as on the axios half; the caller still sees the 403.
	if (action === "enrol") void requireEnrolment(details);
	// "stepup" is answered like "fail": no route reached through httpFetch is declared stepup:, so there is nothing to prompt for.
	return action === "retry" && isReplayable(init.body) ? send() : response;
};
